// First Created - 19 Aug 2026
// Author - Atul Pandey
// Purpose - API slice for Org Trail Session Challenge Assignments
// Updated - 20 Aug 2026 - Get User Session Challenge Assignments

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'

import {
    apiSlice,
} from '@/app/api/apiSlice'

const orgTrailSessionChallengeAssignmentsAdapter =
    createEntityAdapter({})

const initialState =
    orgTrailSessionChallengeAssignmentsAdapter
        .getInitialState()

export const orgTrailSessionChallengeAssignmentsApiSlice =
    apiSlice.injectEndpoints({
        endpoints:
            builder => ({

                /*
                |--------------------------------------------------------------------------
                | 01 - Get Challenge Assignments For Cohort Session
                |--------------------------------------------------------------------------
                */

                getOrgTrailSessionChallengeAssignments:
                    builder.query({
                        query: ({
                            orgId = '',
                            trailId = '',
                            sessionId = '',
                            cohortId = '',
                            active = '',
                        } = {}) => ({
                            url:
                                '/orgTrailSessionChallengeAssignments/getOrgTrailSessionChallengeAssignments',

                            params: {
                                ...(orgId
                                    ? { orgId }
                                    : {}),

                                ...(trailId
                                    ? { trailId }
                                    : {}),

                                ...(sessionId
                                    ? { sessionId }
                                    : {}),

                                ...(cohortId
                                    ? { cohortId }
                                    : {}),

                                ...(active !== ''
                                    ? { active }
                                    : {}),
                            },

                            validateStatus: (
                                response,
                                result
                            ) => {
                                return (
                                    response.status === 200 &&
                                    !result?.isError
                                )
                            },
                        }),

                        /*
                        |--------------------------------------------------------------------------
                        | Backend Response
                        |--------------------------------------------------------------------------
                        |
                        | {
                        |     count,
                        |     assignments
                        | }
                        |
                        |--------------------------------------------------------------------------
                        */

                        transformResponse:
                            responseData => {
                                const assignments =
                                    Array.isArray(
                                        responseData
                                            ?.assignments
                                    )
                                        ?
                                        responseData.assignments
                                        :
                                        []

                                const loadedAssignments =
                                    assignments.map(
                                        assignment => ({
                                            ...assignment,

                                            id:
                                                assignment._id,
                                        })
                                    )

                                return orgTrailSessionChallengeAssignmentsAdapter
                                    .setAll(
                                        initialState,
                                        loadedAssignments
                                    )
                            },

                        providesTags: (
                            result
                        ) => {
                            if (
                                result?.ids
                            ) {
                                return [
                                    {
                                        type:
                                            'OrgTrailSessionChallengeAssignment',

                                        id:
                                            'LIST',
                                    },

                                    ...result.ids.map(
                                        id => ({
                                            type:
                                                'OrgTrailSessionChallengeAssignment',

                                            id,
                                        })
                                    ),
                                ]
                            }

                            return [
                                {
                                    type:
                                        'OrgTrailSessionChallengeAssignment',

                                    id:
                                        'LIST',
                                },
                            ]
                        },
                    }),

                /*
                |--------------------------------------------------------------------------
                | 02 - Get One Challenge Assignment
                |--------------------------------------------------------------------------
                |
                | Useful for Update / Delete pages.
                |
                |--------------------------------------------------------------------------
                */

                getOrgTrailSessionChallengeAssignment:
                    builder.query({
                        query: ({
                            orgId,
                            trailId,
                            sessionId,
                            challengeId,
                            cohortId,
                        }) => ({
                            url:
                                '/orgTrailSessionChallengeAssignments/getOrgTrailSessionChallengeAssignment',

                            params: {
                                orgId,
                                trailId,
                                sessionId,
                                challengeId,
                                cohortId,
                            },

                            validateStatus: (
                                response,
                                result
                            ) => {
                                return (
                                    response.status === 200 &&
                                    !result?.isError
                                )
                            },
                        }),

                        /*
                        |--------------------------------------------------------------------------
                        | Backend May Return:
                        |
                        | assignment: null
                        | effectiveAudienceType: AllCohortUsers
                        | isExplicitAssignment: false
                        |
                        | This is intentionally preserved.
                        |--------------------------------------------------------------------------
                        */

                        transformResponse:
                            responseData => ({
                                ...responseData,

                                assignment:
                                    responseData
                                        ?.assignment
                                        ?
                                        {
                                            ...responseData.assignment,

                                            id:
                                                responseData
                                                    .assignment
                                                    ._id,
                                        }
                                        :
                                        null,
                            }),

                        providesTags: (
                            result
                        ) => {
                            const assignmentId =
                                result
                                    ?.assignment
                                    ?._id

                            if (
                                assignmentId
                            ) {
                                return [
                                    {
                                        type:
                                            'OrgTrailSessionChallengeAssignment',

                                        id:
                                            assignmentId,
                                    },
                                ]
                            }

                            return [
                                {
                                    type:
                                        'OrgTrailSessionChallengeAssignment',

                                    id:
                                        'LIST',
                                },
                            ]
                        },
                    }),
                
                /*
                |--------------------------------------------------------------------------
                | Get User Session Challenge Assignments - 20 Aug 2026
                |--------------------------------------------------------------------------
                */

                getUserSessionChallengeAssignments:
                    builder.query({
                        query: ({
                            orgId,
                            trailId,
                            sessionId,
                            cohortId,
                        }) => ({
                            url:
                                '/orgTrailSessionChallengeAssignments/getUserSessionChallengeAssignments',

                            params: {
                                orgId,
                                trailId,
                                sessionId,
                                cohortId,
                            },
                        }),

                        providesTags: (
                            result
                        ) => {

                            const assignments =
                                result
                                    ?.assignments ||
                                []

                            return [
                                ...assignments.map(
                                    assignment => ({
                                        type:
                                            'OrgTrailSessionChallengeAssignment',

                                        id:
                                            assignment._id,
                                    })
                                ),

                                {
                                    type:
                                        'OrgTrailSessionChallengeAssignment',

                                    id:
                                        'USER_SESSION',
                                },
                            ]
                        },
                    }),

                /*
                |--------------------------------------------------------------------------
                | 03 - Create / Update Challenge Assignment
                |--------------------------------------------------------------------------
                |
                | Backend upsert determines whether this is:
                |
                | create
                | update
                | no material change
                |
                |--------------------------------------------------------------------------
                */

                upsertOrgTrailSessionChallengeAssignment:
                    builder.mutation({
                        query:
                            initialAssignment => ({
                                url:
                                    '/orgTrailSessionChallengeAssignments/upsertOrgTrailSessionChallengeAssignment',

                                method:
                                    'PUT',

                                body: {
                                    ...initialAssignment,
                                },
                            }),

                        invalidatesTags: (
                            result
                        ) => {
                            const assignmentId =
                                result
                                    ?.assignment
                                    ?._id

                            return [
                                    {
                                        type:
                                            'OrgTrailSessionChallengeAssignment',

                                        id:
                                            'LIST',
                                    },

                                    // 20 Aug 2026 - refresh runtime user assignment queries
                                    {
                                        type:
                                            'OrgTrailSessionChallengeAssignment',

                                        id:
                                            'USER_SESSION',
                                    },

                                    ...(assignmentId
                                        ?
                                        [
                                            {
                                                type:
                                                    'OrgTrailSessionChallengeAssignment',

                                                id:
                                                    assignmentId,
                                            },
                                        ]
                                        :
                                        []),
                                ]
                        },
                }),

                deleteOrgTrailSessionChallengeAssignment:
                    builder.mutation({
                    query: ({
                        id,
                    }) => ({
                        url:
                            '/orgTrailSessionChallengeAssignments/deleteOrgTrailSessionChallengeAssignment',

                        method:
                            'DELETE',

                        body: {
                            id,
                        },
                    }),

                    invalidatesTags: (
                            result,
                            error,
                            arg
                        ) => [
                            {
                                type:
                                    'OrgTrailSessionChallengeAssignment',

                                id:
                                    arg.id,
                            },

                            {
                                type:
                                    'OrgTrailSessionChallengeAssignment',

                                id:
                                    'LIST',
                            },

                            // 20 Aug 2026 - refresh runtime user assignment queries
                            {
                                type:
                                    'OrgTrailSessionChallengeAssignment',

                                id:
                                    'USER_SESSION',
                            },
                        ],
                }),
            }),
    })

/*
|--------------------------------------------------------------------------
| 04 - Hooks
|--------------------------------------------------------------------------
*/

export const {
    useGetOrgTrailSessionChallengeAssignmentsQuery,

    useGetOrgTrailSessionChallengeAssignmentQuery,

    useLazyGetOrgTrailSessionChallengeAssignmentQuery,

    useGetUserSessionChallengeAssignmentsQuery,             // 20 Aug 2026

    useLazyGetUserSessionChallengeAssignmentsQuery,         // 20 Aug 2026

    useUpsertOrgTrailSessionChallengeAssignmentMutation,

    useDeleteOrgTrailSessionChallengeAssignmentMutation,
} =
    orgTrailSessionChallengeAssignmentsApiSlice

/*
|--------------------------------------------------------------------------
| 05 - Base Selector
|--------------------------------------------------------------------------
*/

export const selectOrgTrailSessionChallengeAssignmentsResult =
    orgTrailSessionChallengeAssignmentsApiSlice
        .endpoints
        .getOrgTrailSessionChallengeAssignments
        .select()

/*
|--------------------------------------------------------------------------
| 06 - Select Assignment Data
|--------------------------------------------------------------------------
*/

const selectOrgTrailSessionChallengeAssignmentsData =
    createSelector(
        selectOrgTrailSessionChallengeAssignmentsResult,

        assignmentResult =>
            assignmentResult.data
    )

/*
|--------------------------------------------------------------------------
| 07 - Entity Selectors
|--------------------------------------------------------------------------
*/

export const {
    selectAll:
        selectAllOrgTrailSessionChallengeAssignments,

    selectById:
        selectOrgTrailSessionChallengeAssignmentById,

    selectIds:
        selectOrgTrailSessionChallengeAssignmentIds,
} =
    orgTrailSessionChallengeAssignmentsAdapter
        .getSelectors(
            state =>
                selectOrgTrailSessionChallengeAssignmentsData(
                    state
                ) ??
                initialState
        )